import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useFormContext, useWatch } from 'react-hook-form';
import { useDealBuilderContext } from '../context/DealBuilderContext';
import { getContactId } from '../lib';
import { DealBuilderFormValues, EmailOptionGroups, SendEmailOption } from '../types';
import { Account } from 'types';

export interface CcEmailOptionsResult {
  options: SendEmailOption[];
  defaultCcEmails: SendEmailOption[];
}

export const useCcEmailOptions = (): CcEmailOptionsResult => {
  const {
    sourceData: { vo },
    contacts: { contactsData },
  } = useDealBuilderContext();
  const userProfile = useSelector((state: any) => state.userProfile);
  const accountContacts: Account['contacts'] = useSelector((state: any) => state.account?.contacts ?? []);
  const { control } = useFormContext<DealBuilderFormValues>();
  const ownerPgIds = useWatch({ control, name: 'contacts.ownerPgIds' });
  const accountContactIds = useWatch({ control, name: 'contacts.accountContactIds' });
  const sendEmailTo = useWatch({ control, name: 'reviewSend.sendEmailTo' });

  return useMemo(() => {
    const selectedIds = new Set([...ownerPgIds, ...accountContactIds]);
    const sendToEmails = new Set((sendEmailTo ?? []).map((o: SendEmailOption) => o.email));

    const dealershipContacts: SendEmailOption[] = [vo?.salesRepresentative, vo?.salesManager, vo?.financeManager]
      .filter((c): c is NonNullable<typeof c> => !!c?.email && c?.status !== 'blocked')
      .map(c => ({
        label: `${c.firstName} ${c.lastName}`,
        value: c.email,
        fullName: `${c.firstName} ${c.lastName}`,
        email: c.email,
        title: c.title ?? '',
        group: EmailOptionGroups.DealershipContacts,
      }));

    const dealershipEmails = new Set(dealershipContacts.map(c => c.email));

    const customerContacts: SendEmailOption[] = contactsData.allContacts
      .filter(c => !selectedIds.has(getContactId(c)) && !!c.email && !sendToEmails.has(c.email as string))
      .map(c => ({
        label: c.fullName,
        value: c.email as string,
        fullName: c.fullName,
        email: c.email as string,
        title: c.title ?? '',
        group: EmailOptionGroups.CustomerContacts,
      }));

    const otherContacts: SendEmailOption[] = accountContacts
      .filter(c => !!c.email && !dealershipEmails.has(c.email) && !sendToEmails.has(c.email) && c.status !== 'blocked')
      .map(c => ({
        label: c.fullName ?? [c.firstName, c.lastName].filter(Boolean).join(' '),
        value: c.email as string,
        fullName: c.fullName ?? [c.firstName, c.lastName].filter(Boolean).join(' '),
        email: c.email as string,
        title: c.title ?? '',
        group: EmailOptionGroups.OtherContacts,
      }));

    const filteredDealershipContacts = dealershipContacts.filter(c => !sendToEmails.has(c.email));

    const currentContact = otherContacts.find(c => c.email === userProfile?.email);

    return {
      options: [...customerContacts, ...filteredDealershipContacts, ...otherContacts],
      defaultCcEmails: [...customerContacts, ...(currentContact ? [currentContact] : [])],
    };
  }, [vo, contactsData, accountContacts, ownerPgIds, accountContactIds, sendEmailTo]);
};
